<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { useUISettings, useConversationSidebarItemsOrder, DEFAULT_CONVERSATION_SIDEBAR_ITEMS_ORDER } from 'dashboard/composables/useUISettings';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';

import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import ContactConversations from './ContactConversations.vue';
import ContactInfo from './contact/ContactInfo.vue';
import ContactNotes from './contact/ContactNotes.vue';
import ContactTarefasSection from './contact/ContactTarefasSection.vue';
import ConversationAction from './ConversationAction.vue';
import ConversationInfo from './ConversationInfo.vue';
import ConversationParticipant from './ConversationParticipant.vue';
import CustomAttributes from './customAttributes/CustomAttributes.vue';
import Draggable from 'vuedraggable';
import LinearIssuesList from 'dashboard/components/widgets/conversation/linear/LinearIssueItem.vue';
import LinearSetupCTA from 'dashboard/components/widgets/conversation/linear/LinearSetupCTA.vue';
import MacrosList from './Macros/List.vue';
import SharedFiles from './SharedFiles.vue';
import ShopifyOrdersList from 'dashboard/components/widgets/conversation/ShopifyOrdersList.vue';
import SidebarActionsHeader from 'dashboard/components-next/SidebarActionsHeader.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  onToggleCollapse: {
    type: Function,
    default: () => {},
  },
});

const store = useStore();
const route = useRoute();
const { uiSettings, updateUISettings } = useUISettings();
const { isFeatureEnabledonAccount } = useAccount();

const dragging = ref(false);
const conversationSidebarItemsOrder = useConversationSidebarItemsOrder(uiSettings);
const conversationSidebarItems = ref([...(conversationSidebarItemsOrder.value || DEFAULT_CONVERSATION_SIDEBAR_ITEMS_ORDER)]);

watch(
  conversationSidebarItemsOrder,
  newOrder => {
    if (newOrder && newOrder.length) {
      conversationSidebarItems.value = [...newOrder];
    }
  },
  { immediate: true }
);

const isContactSidebarItemOpen = key => {
  const { [key]: isOpen } = uiSettings.value;
  return !!isOpen;
};

const toggleSidebarUIState = key => {
  updateUISettings({ [key]: !isContactSidebarItemOpen(key) });
};

const isLinearConnected = computed(() => {
  return store.getters['integrations/getLinearIntegrationState'];
});

const isLinearClientIdConfigured = computed(() => {
  return store.getters['globalConfig/getLinearClientId'];
});

const isLinearFeatureEnabled = computed(() =>
  isFeatureEnabledonAccount('linear_integration')
);
const isShopifyFeatureEnabled = computed(() =>
  isFeatureEnabledonAccount('shopify_integration')
);

const conversationGetter = useMapGetter('getSelectedChat');
const contactGetter = useMapGetter('contacts/getContact');

const channelType = computed(() => conversationGetter.value.meta?.channel);
const contactId = computed(() => conversationGetter.value.meta?.sender?.id);
const inboxId = computed(() => conversationGetter.value.inbox_id);
const conversationAdditionalAttributes = computed(
  () => conversationGetter.value.additional_attributes || {}
);

const contact = computed(() => contactGetter.value(contactId.value) || {});
const contactAdditionalAttributes = computed(
  () => contact.value.additional_attributes || {}
);

const getContactDetails = () => {
  if (contactId.value) {
    store.dispatch('contacts/show', { id: contactId.value });
  }
};

watch(contactId, (newContactId, prevContactId) => {
  if (newContactId && newContactId !== prevContactId) {
    getContactDetails();
  }
});

const onDragEnd = () => {
  dragging.value = false;
  updateUISettings({
    conversation_sidebar_items_order: conversationSidebarItems.value,
  });
};

const closeContactPanel = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
  });
};

onMounted(() => {
  conversationSidebarItems.value = conversationSidebarItemsOrder.value;
  getContactDetails();
  store.dispatch('attributes/get', 0);
  store.dispatch('integrations/get', 'linear');
});
</script>

<template>
  <div class="w-full overflow-y-auto no-scrollbar">
    <SidebarActionsHeader
      :title="$t('CONVERSATION.SIDEBAR.CONTACT')"
      @close="closeContactPanel"
    />
    
    <!-- Profile Header (Avatar, Contact Info, Action buttons) -->
    <ContactInfo :contact="contact" :channel-type="channelType" />

    <!-- Dedicated Prazos & Tarefas Section (Open Only by Default) -->
    <ContactTarefasSection
      v-if="contact.id"
      :contact-id="contact.id"
      :conversation-id="conversationId"
      :contact="contact"
    />

    <!-- Secondary Attributes & Accordions -->
    <div class="px-2 pb-8 list-group pt-2">
      <Draggable
        :list="conversationSidebarItems"
        animation="200"
        ghost-class="ghost"
        handle=".drag-handle"
        item-key="name"
        class="flex flex-col gap-3"
        @start="dragging = true"
        @end="onDragEnd"
      >
        <template #item="{ element }">
          <div
            v-if="element.name === 'conversation_actions'"
            class="conversation--actions"
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_ACTIONS')"
              :is-open="isContactSidebarItemOpen('is_conv_actions_open')"
              @toggle="toggleSidebarUIState('is_conv_actions_open')"
            >
              <ConversationAction
                :conversation-id="conversationId"
                :inbox-id="inboxId"
              />
            </AccordionItem>
          </div>
          <div
            v-else-if="element.name === 'conversation_participants'"
            class="conversation--actions"
          >
            <AccordionItem
              :title="$t('CONVERSATION_PARTICIPANTS.SIDEBAR_TITLE')"
              :is-open="isContactSidebarItemOpen('is_conv_participants_open')"
              @toggle="toggleSidebarUIState('is_conv_participants_open')"
            >
              <ConversationParticipant
                :conversation-id="conversationId"
                :inbox-id="inboxId"
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'conversation_info'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_INFO')"
              :is-open="isContactSidebarItemOpen('is_conv_details_open')"
              compact
              @toggle="toggleSidebarUIState('is_conv_details_open')"
            >
              <ConversationInfo
                :conversation-attributes="conversationAdditionalAttributes"
                :contact-attributes="contactAdditionalAttributes"
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'contact_attributes'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_ATTRIBUTES')"
              :is-open="isContactSidebarItemOpen('is_contact_attributes_open')"
              compact
              @toggle="toggleSidebarUIState('is_contact_attributes_open')"
            >
              <CustomAttributes
                attribute-type="contact_attribute"
                attribute-from="conversation_contact_panel"
                :contact-id="contact.id"
                :empty-state-message="$t('CONVERSATION_CUSTOM_ATTRIBUTES.NO_RECORDS_FOUND')"
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'previous_conversation'">
            <AccordionItem
              v-if="contact.id"
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.PREVIOUS_CONVERSATION')"
              :is-open="isContactSidebarItemOpen('is_previous_conv_open')"
              compact
              @toggle="toggleSidebarUIState('is_previous_conv_open')"
            >
              <ContactConversations
                :contact-id="contact.id"
                :conversation-id="conversationId"
              />
            </AccordionItem>
          </div>
          <woot-feature-toggle
            v-else-if="element.name === 'macros'"
            feature-key="macros"
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.MACROS')"
              :is-open="isContactSidebarItemOpen('is_macro_open')"
              compact
              @toggle="toggleSidebarUIState('is_macro_open')"
            >
              <MacrosList :conversation-id="conversationId" />
            </AccordionItem>
          </woot-feature-toggle>
          <div
            v-else-if="
              element.name === 'linear_issues' &&
              isLinearFeatureEnabled &&
              isLinearClientIdConfigured
            "
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.LINEAR_ISSUES')"
              :is-open="isContactSidebarItemOpen('is_linear_issues_open')"
              compact
              @toggle="toggleSidebarUIState('is_linear_issues_open')"
            >
              <LinearSetupCTA v-if="!isLinearConnected" />
              <LinearIssuesList v-else :conversation-id="conversationId" />
            </AccordionItem>
          </div>
          <div
            v-else-if="
              element.name === 'shopify_orders' && isShopifyFeatureEnabled
            "
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.SHOPIFY_ORDERS')"
              :is-open="isContactSidebarItemOpen('is_shopify_orders_open')"
              compact
              @toggle="toggleSidebarUIState('is_shopify_orders_open')"
            >
              <ShopifyOrdersList :contact-id="contactId" />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'contact_notes'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_NOTES')"
              :is-open="isContactSidebarItemOpen('is_contact_notes_open')"
              compact
              @toggle="toggleSidebarUIState('is_contact_notes_open')"
            >
              <ContactNotes :contact-id="contactId" />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'shared_files'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.SHARED_FILES')"
              :is-open="isContactSidebarItemOpen('is_shared_files_open')"
              compact
              @toggle="toggleSidebarUIState('is_shared_files_open')"
            >
              <SharedFiles />
            </AccordionItem>
          </div>
        </template>
      </Draggable>
    </div>
  </div>
</template>

<style lang="scss" scoped>
:deep(.contact--profile) {
  @apply pb-3 border-b border-solid border-n-weak;
}
</style>
